import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PageHero, PageCta } from "@/components/PageParts";
import { usePageMeta } from "@/hooks/usePageMeta";
import { services } from "@/data/services";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

export default function ServicesIndex() {
  usePageMeta(
    "Services — May Forward Tech",
    "AI strategy and consulting, custom AI implementation, custom development, digital transformation and high-ROI marketing for enterprises."
  );
  return (
    <>
      <PageHero
        eyebrow="What we do"
        title="Software built for you, not everyone"
        intro="Big Tech doesn't know your organization's needs. We do. Choose a discipline to see how we work, what you get, and the outcomes we commit to."
      />
      <section className="py-16 lg:py-24 bg-[#FAFAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {services.map((s, i) => (
            <motion.div key={s.slug} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.06 }}>
              <Link
                to={`/services/${s.slug}`}
                data-testid={`service-tile-${s.slug}`}
                className="bento-card group flex flex-col rounded-2xl border border-slate-200 bg-white overflow-hidden h-full"
              >
                <div className="h-44 lg:h-52 overflow-hidden bg-white border-b border-slate-100">
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <span className="self-start font-mono-tech text-[10px] uppercase tracking-[0.2em] text-[#0FA89E] border border-[rgba(15,168,158,0.25)] rounded-full px-3 py-1 mb-4">
                    {s.badge}
                  </span>
                  <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-[#0F172A] mb-3">
                    {s.title}
                  </h2>
                  <p className="text-sm text-slate-600 leading-relaxed mb-6">{s.outcome}</p>
                  <span className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-[#0FA89E]">
                    Explore this service
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
      <PageCta />
    </>
  );
}
